import React, { Fragment, useEffect, useMemo, useState } from "react";
import styled, { css } from "styled-components";
import useLoader from "../../../hooks/useLoader";
import { useSnackbar } from "notistack";
import { getErrorMessage } from "../../../helpers/functions";
import CaseService from "../../../services/CaseService";
import { Formik } from "formik";
import {
  PrimaryCTAButton,
  PrimaryOutlinedCloseCTAButton,
} from "../../common/Buttons";
import { Modal, Tooltip } from "@material-ui/core";
import PeriodSelector, { PERIOD_MODES } from "./PeriodSelector";
import FormField from "../../common/FormField/FormField";
import {
  Overlay,
  ModalCard,
  ModalHeader,
  ModalBody,
  ModalTitle,
  FooterActions,
  FormSection,
  HeaderContent,
  IconBadge,
  CloseBtn,
  SectionLabel,
  ModeToggle,
  ModeBtn,
} from "../../common/MutipleHearings";
import moment from "moment";
import { reportsValidationSchema } from "../../../helpers/validationSchema";
import theme from "../../../assets/theme";
import COLORS from "../../../assets/Colors";
import PartyService from "../../../services/PartyService";
import CustomSelect from "../NoticeGeneration/component/CustomSelect";

const resolutionKindOptions = [
  { label: "Arbitration", value: "arbitration" },
  { label: "Mediation", value: "mediation" },
  { label: "Negotiation", value: "negotiation" },
];

const PARTY_SEARCH_MODES = {
  ALL: "all",
  PARTY: "party",
  PARTY_CODE: "partyCode",
};

const partySearchOptions = [
  { label: "All Parties", value: PARTY_SEARCH_MODES.ALL },
  { label: "Party", value: PARTY_SEARCH_MODES.PARTY },
  { label: "Party Code", value: PARTY_SEARCH_MODES.PARTY_CODE },
];

const radio_inputs = [
  {
    name: "arbitratorReport",
    label: "Arbitrator Report",
    value: "arbitratorReport",
    description:
      "Shows the number of cases handled by each arbitrator, categorized by the current case stage/status.",
  },
  {
    name: "caseStatisticsReport",
    label: "Case Statistics Reports",
    value: "caseStatisticsReport",
    description:
      "Provides case-level details including Case ID, Contract Number, Claim Value, Claimant Name, Respondent Name, and Created Date.",
  },
  {
    name: "caseAgeingReport",
    label: "Case Ageing Report",
    value: "caseAgeingReport",
    description:
      "Shows the number of days each case has been pending, calculated from the case creation date up to the current date.",
  },
  {
    name: "caseStagesAgeingReport",
    label: "Case Stages Ageing Report",
    value: "caseStagesAgeingReport",
    description:
      "Shows the time taken by a case at each stage, calculated from the date the case entered/was created in that stage until the stage was completed.",
  },
  {
    name: "awardStatusReport",
    label: "Award Status Report",
    value: "awardStatusReport",
    description:
      "Lists all cases for which an award has been passed, along with relevant case and award details.",
  },
  {
    name: "mediatorList",
    label: "Mediator Details",
    value: "mediatorList",
    description: "Lists all empanelled mediators and their relevant details.",
  },
  {
    name: "arbitratorList",
    label: "Arbitrator Details",
    value: "arbitratorList",
    description: "Lists all empanelled arbitrators and their relevant details.",
  },
];

const CaseReports = ({ state, setState, options }) => {
  const { setLoader } = useLoader();
  const [activeTip, setActiveTip] = useState(null);

  const initialValues = useMemo(
    () => ({
      resolutionKind: "arbitration",
      mode: PERIOD_MODES.RANGE,
      fromDate: new Date(),
      toDate: new Date(),
      year: new Date().getFullYear(),
      month: new Date().getMonth() + 1,
      reportsType: "arbitratorReport",
      partySearchMode: PARTY_SEARCH_MODES.ALL,
      partyId: [],
      partyCode: "",
    }), // eslint-disable-next-line react-hooks/exhaustive-deps
    [state?.state],
  );
  const { enqueueSnackbar } = useSnackbar();
  const [partyCodes, setPartyCodes] = useState([]);

  useEffect(() => {
    async function loadPartyCodes() {
      try {
        setLoader({ state: true, message: "Loading party codes..." });
        const res = await PartyService.getPartyCodes();
        setPartyCodes(
          res.map((p) => ({ label: p.customerCode, value: p.customerCode })),
        );
      } catch (err) {
        enqueueSnackbar("Failed to load party codes", { variant: "error" });
      } finally {
        setLoader({ state: false });
      }
    }
    loadPartyCodes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function onFormSubmit(values) {
    try {
      setLoader({ state: true });

      let payload = {
        reportsType: values.reportsType,
        resolutionKind: values?.resolutionKind,
      };

      const hasPartyId = Array.isArray(values.partyId)
        ? values.partyId.length > 0
        : !!values.partyId;
      const hasPartyCode = !!values.partyCode;

      if (values.partySearchMode === PARTY_SEARCH_MODES.PARTY && hasPartyId) {
        payload.partyId = values.partyId;
      } else if (
        values.partySearchMode === PARTY_SEARCH_MODES.PARTY_CODE &&
        hasPartyCode
      ) {
        payload.partyCode = values.partyCode;
      }

      if (values.mode === PERIOD_MODES.RANGE) {
        payload = {
          ...payload,
          fromDate: moment(new Date(values.fromDate)).format("DD/MM/YYYY"),
          toDate: moment(new Date(values.toDate)).format("DD/MM/YYYY"),
        };
      } else if (values.mode === PERIOD_MODES.MONTH) {
        payload = {
          ...payload,
          month: values.month,
          year: values.year,
        };
      } else if (values.mode === PERIOD_MODES.YEAR) {
        payload = {
          ...payload,
          year: values.year,
        };
      }
      const response = await CaseService.reportsDownload(payload);
      if (response) {
        window.open(response?.reportFile);
      }
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error), { variant: "error" });
    } finally {
      setLoader({ state: false });
      setState({ state: false });
    }
  }

  return (
    <Fragment>
      <Modal open={state?.state} onClose={() => setState({ state: false })}>
        <Overlay>
          <ModalCard>
            <ModalHeader>
              <HeaderContent>
                <IconBadge>
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                    <line x1="8" y1="13" x2="16" y2="13" />
                    <line x1="8" y1="17" x2="13" y2="17" />
                  </svg>
                </IconBadge>
                <div>
                  <ModalTitle>Reports</ModalTitle>
                  <SubTitle>Generate and download case reports</SubTitle>
                </div>
              </HeaderContent>
              <CloseBtn
                onClick={() => setState({ state: false })}
                aria-label="Close"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </CloseBtn>
            </ModalHeader>

            <Formik
              initialValues={initialValues}
              onSubmit={onFormSubmit}
              validationSchema={reportsValidationSchema}
              enableReinitialize
            >
              {({
                values,
                handleChange,
                errors,
                handleSubmit,
                touched,
                handleBlur,
                setFieldValue,
              }) => (
                <FormLayout onSubmit={handleSubmit}>
                  <ModalBody>
                    <FormSection>
                      <SectionLabel>Select Period</SectionLabel>
                      <PeriodSelector
                        {...{
                          values,
                          errors,
                          touched,
                          handleBlur,
                          handleChange,
                          setFieldValue,
                        }}
                      />
                    </FormSection>

                    <FormSection>
                      <LabelRow>
                        <SectionLabel style={{ margin: 0 }}>
                          Filter By Party
                        </SectionLabel>
                        <OptionalTag>Optional</OptionalTag>
                      </LabelRow>
                      <ModeToggle>
                        {partySearchOptions.map((opt) => (
                          <ModeBtn
                            key={opt.value}
                            type="button"
                            active={values.partySearchMode === opt.value}
                            onClick={() => {
                              setFieldValue("partySearchMode", opt.value);
                              setFieldValue("partyId", []);
                              setFieldValue("partyCode", "");
                            }}
                          >
                            {opt.label}
                          </ModeBtn>
                        ))}
                      </ModeToggle>

                      {values.partySearchMode === PARTY_SEARCH_MODES.ALL && (
                        <HintText>
                          Report will include all parties. Choose Party or Party
                          Code to narrow it down.
                        </HintText>
                      )}

                      {values.partySearchMode === PARTY_SEARCH_MODES.PARTY && (
                        <FieldWrap>
                          <CustomSelect
                            name="partyId"
                            trigger={true}
                            required={false}
                            getNoticeTemplates={() => {}}
                            errors={errors}
                            touched={touched}
                            setFieldValue={setFieldValue}
                            menuItems={options}
                          />
                          <HintText>
                            Leave empty to include all parties.
                          </HintText>
                        </FieldWrap>
                      )}

                      {values.partySearchMode ===
                        PARTY_SEARCH_MODES.PARTY_CODE && (
                        <FieldWrap>
                          <FormField
                            input={{
                              type: "select",
                              name: "partyCode",
                              label: "Select Party Code",
                              required: false,
                            }}
                            menuItems={partyCodes}
                            white
                            {...{
                              values,
                              errors,
                              touched,
                              handleBlur,
                              handleChange,
                              setFieldValue,
                            }}
                          />
                          <HintText>
                            Leave empty to include all party codes.
                          </HintText>
                        </FieldWrap>
                      )}
                    </FormSection>

                    <FormSection>
                      <SectionLabel>Case Type</SectionLabel>
                      <ModeToggle>
                        {resolutionKindOptions.map((opt) => (
                          <ModeBtn
                            key={opt.value}
                            type="button"
                            active={values.resolutionKind === opt.value}
                            onClick={() =>
                              setFieldValue("resolutionKind", opt.value)
                            }
                          >
                            {opt.label}
                          </ModeBtn>
                        ))}
                      </ModeToggle>
                    </FormSection>

                    <FormSection>
                      <SectionLabel>Select Report</SectionLabel>
                      <ReportGrid role="radiogroup">
                        {radio_inputs.map((radio) => {
                          const selected = values.reportsType === radio.value;
                          return (
                            <Tooltip
                              key={radio.value}
                              title={radio.description}
                              placement="top"
                              arrow
                              open={activeTip === radio.value}
                              onOpen={() => setActiveTip(radio.value)}
                              onClose={() => setActiveTip(null)}
                            >
                              <ReportTile
                                type="button"
                                role="radio"
                                aria-checked={selected}
                                selected={selected}
                                onClick={() => {
                                  setFieldValue("reportsType", radio.value);
                                  setActiveTip(radio.value);
                                }}
                              >
                                <RadioDot selected={selected} />
                                <span
                                  style={{
                                    fontFamily: theme.fonts.primaryFontSemiBold,
                                    color: COLORS.COLOR_DARK,
                                  }}
                                >
                                  {radio.label}
                                </span>
                              </ReportTile>
                            </Tooltip>
                          );
                        })}
                      </ReportGrid>
                    </FormSection>
                  </ModalBody>

                  <StickyFooter>
                    <PrimaryOutlinedCloseCTAButton
                      type="button"
                      onClick={() => setState({ state: false })}
                      style={{ flex: 1 }}
                    >
                      Cancel
                    </PrimaryOutlinedCloseCTAButton>
                    <PrimaryCTAButton
                      type="submit"
                      disabled={false}
                      onClick={handleSubmit}
                      style={{ flex: 1 }}
                    >
                      Download Report
                    </PrimaryCTAButton>
                  </StickyFooter>
                </FormLayout>
              )}
            </Formik>
          </ModalCard>
        </Overlay>
      </Modal>
    </Fragment>
  );
};

export default CaseReports;

// Form spans body + footer so header/footer stay fixed and only ModalBody scrolls
const FormLayout = styled.form`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  width: 100%;
`;

const StickyFooter = styled(FooterActions)`
  flex-shrink: 0;
  margin-top: 0;
  padding: 16px 24px;
  background: #fff;
  border-radius: 0 0 20px 20px;
  @media (max-width: 480px) {
    padding: 12px 16px;
    border-radius: 0;
  }
`;

const SubTitle = styled.div`
  margin-top: 2px;
  font-family: ${theme.fonts.primaryFontRegular};
  font-size: 12px;
  color: #6b7280;
`;

const LabelRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
`;

const OptionalTag = styled.span`
  padding: 2px 8px;
  border-radius: 99px;
  background: #f4f6f8;
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 10px;
  color: #6b7280;
  letter-spacing: 0.3px;
`;

const FieldWrap = styled.div`
  margin-top: 12px;
`;

const HintText = styled.div`
  margin-top: 8px;
  font-family: ${theme.fonts.primaryFontRegular};
  font-size: 12px;
  color: #9ca3af;
`;

const ReportGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const ReportTile = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  border-radius: 12px;
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 13px;
  text-align: left;
  cursor: pointer;
  transition: all 0.18s ease;
  ${(p) =>
    p.selected
      ? css`
          border: 1.5px solid ${COLORS.BTN_GREEN};
          background: ${COLORS.BTN_GREEN}0d;
          color: ${COLORS.COLOR_DARK};
          box-shadow: 0 2px 8px ${COLORS.BTN_GREEN}1f;
        `
      : css`
          border: 1.5px solid #e5e7eb;
          background: #fff;
          color: #4b5563;
          &:hover {
            border-color: #cbd5e1;
            background: #f9fafb;
          }
        `}
  &:focus-visible {
    outline: 2px solid ${COLORS.BTN_GREEN};
    outline-offset: 2px;
  }
`;

const RadioDot = styled.span`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  flex-shrink: 0;
  transition: all 0.18s ease;
  border: 2px solid ${(p) => (p.selected ? COLORS.BTN_GREEN : "#d1d5db")};
  box-shadow: inset 0 0 0 ${(p) => (p.selected ? "3px" : "0")} #fff;
  background: ${(p) => (p.selected ? COLORS.BTN_GREEN : "#fff")};
`;
